"use client"

import * as React from "react"
import LogoIcon from "@/assets/logo-icon.svg"
import LogoText from "@/assets/logo-text.svg"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar"
import {
  LucideHome,
  FileVolume,
  LucideReceipt,
  LifeBuoy,
  MessageCircleQuestion,
  ShieldAlert,
  ReceiptText,
  SquarePlus,
  Newspaper,
  TriangleAlert,
} from "lucide-react"
import { Link, useLocation, useNavigate } from "@tanstack/react-router"
import { Button } from "@/components/ui/button.tsx"
import { useUser } from "@/contexts/UserContext.tsx"
import { useTranslation } from "react-i18next"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx"
import { useState } from "react"
import { Label } from "@/components/ui/label.tsx"
import { Input } from "@/components/ui/input.tsx"
import { Textarea } from "@/components/ui/textarea.tsx"
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.tsx"
import { useMutation } from "@tanstack/react-query"
import { userApi } from "@/api/user.api.ts"
import { toast } from "@/components/ui/toast.tsx"

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { state, isMobile, toggleSidebar } = useSidebar()
  const navigate = useNavigate()
  const location = useLocation()
  const { locale, user } = useUser()
  const { t } = useTranslation()
  const [isSupportOpen, setIsSupportOpen] = useState(false)
  const [description, setDescription] = useState("")
  const [subject, setSubject] = useState("")
  const [supportType, setSupportType] = useState("enquiries")
  const [isSending, setIsSending] = useState(false)

  const supportTypes = [
    { label: t("support.general"), value: "enquiries" },
    { label: t("support.tech"), value: "tech" },
    { label: t("support.finance"), value: "finance" },
  ]

  const onClickCreate = async () => {
    await navigate({ to: "/pitch/create" })
    onMenuItemClick()
  }

  const onMenuItemClick = () => {
    if (isMobile && state === "expanded") {
      toggleSidebar()
    }
  }

  const onClickSupport = () => {
    setIsSupportOpen(true)
    onMenuItemClick()
  }
  const onClickSend = async () => {
    setIsSending(true)
    try {
      const res = await fetch(
        "https://egab-auto-reply-3422.twil.io/callSupport",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            messageDetails: {
              subject,
              message: description,
              name: `${user.name} ${user.surname}`,
              userEmail: user.emailAddress,
              type: supportType,
            },
          }),
        }
      )

      if (!res.ok) {
        throw new Error(`Request failed: ${res.status}`)
      } else {
        toast.add({
          type: "success",
          description: t("support.confirmation"),
        })
        setIsSupportOpen(false)
        setIsSending(false)
        setSubject("")
        setDescription("")
      }
    } catch (err) {
      console.error(err)
    } finally {
    }
  }

  return (
    <>
      <Sidebar
        dir={locale === "ar" ? "rtl" : "ltr"}
        side={locale === "ar" ? "right" : "left"}
        collapsible="icon"
        {...props}
      >
        <SidebarHeader>
          <div className="mt-2 mb-2">
            <div className="flex h-8 flex-row gap-2 overflow-hidden">
              <img width="34" height="30" src={LogoIcon} alt="" />
              <img width="75" height="30" src={LogoText} alt="" />
            </div>
          </div>
        </SidebarHeader>
        <SidebarContent>
          <SidebarMenuItem
            className={`${locale === "ar" ? "ms-2 me-2" : "me-2"} box-border list-none`}
          >
            <SidebarMenuButton
              className="mb-3 w-full"
              tooltip={t("createBtn")}
              render={<Button onClick={onClickCreate} />}
            >
              <SquarePlus />{" "}
              {(state === "expanded" || isMobile) && t("createBtn")}
            </SidebarMenuButton>
          </SidebarMenuItem>
          {/* <SidebarMenuItem className={`${locale === "ar" ? "ms-2 me-2" : "me-2"} mb-1 box-border list-none`}>*/}
          {/*  <SidebarMenuButton*/}
          {/*    tooltip="Home"*/}
          {/*    isActive={location.href === "/"}*/}
          {/*    render={<Link to={"/"} onClick={onMenuItemClick} />}*/}
          {/*  >*/}
          {/*    <LucideHome />*/}
          {/*    <span>Home</span>*/}
          {/*  </SidebarMenuButton>*/}
          {/*</SidebarMenuItem>*/}
          <SidebarMenuItem
            className={`${locale === "ar" ? "ms-2 me-2" : "me-2"} mb-1 box-border list-none`}
          >
            <SidebarMenuButton
              tooltip={t("sidebar.pitches")}
              isActive={
                (location.href.includes("/pitches?") ||
                  location.href.includes("/pitch/")) &&
                !location.href.includes("/pitch/create")
              }
              render={<Link to={"/pitches"} onClick={onMenuItemClick} />}
            >
              <Newspaper />
              <span>{t("sidebar.pitches")}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem
            className={`${locale === "ar" ? "ms-2 me-2" : "me-2"} mb-1 box-border list-none`}
          >
            <SidebarMenuButton
              tooltip={t("sidebar.calls")}
              isActive={
                location.href.includes("/call/") ||
                location.href.includes("/calls?")
              }
              render={<Link to={"/calls"} onClick={onMenuItemClick} />}
            >
              <FileVolume />
              <span>{t("sidebar.calls")}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem
            className={`${locale === "ar" ? "ms-2 me-2" : "me-2"} mb-1 box-border list-none`}
          >
            <SidebarMenuButton
              tooltip={t("sidebar.commissions")}
              isActive={location.href.includes("/commissions?")}
              render={<Link to={"/commissions"} onClick={onMenuItemClick} />}
            >
              <LucideReceipt />
              <span>{t("sidebar.commissions")}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarContent>
        <SidebarFooter>
          <SidebarMenuItem className="box-border list-none">
            <SidebarMenuButton
              tooltip={t("sidebar.support")}
              render={
                <div className="cursor-pointer" onClick={onClickSupport} />
              }
            >
              <LifeBuoy />
              <span>{t("sidebar.support")}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem className="box-border list-none">
            <SidebarMenuButton
              tooltip={t("sidebar.faq")}
              isActive={location.href === "/faq"}
              render={<Link to={"/faq"} onClick={onMenuItemClick} />}
            >
              <MessageCircleQuestion />
              <span>{t("sidebar.faq")}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem className="box-border list-none">
            <SidebarMenuButton
              tooltip={t("sidebar.safety")}
              isActive={location.href === "/safety"}
              render={<Link to={"/safety"} onClick={onMenuItemClick} />}
            >
              <ShieldAlert />
              <span>{t("sidebar.safety")}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem className="box-border list-none">
            <SidebarMenuButton
              tooltip={t("sidebar.terms")}
              isActive={location.href === "/terms"}
              render={<Link to={"/terms"} onClick={onMenuItemClick} />}
            >
              <ReceiptText />
              <span>{t("sidebar.terms")}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarFooter>
        <SidebarRail />
      </Sidebar>
      <Dialog open={isSupportOpen} onOpenChange={setIsSupportOpen}>
        <DialogContent
          dir={locale === "ar" ? "rtl" : "ltr"}
          className="sm:max-w-md"
        >
          <DialogHeader>
            <DialogTitle>{t("sidebar.support")}</DialogTitle>
          </DialogHeader>
          <div className="w-full space-y-2">
            <Label>{t("support.supportType")}</Label>
            <Select
              items={supportTypes}
              value={supportType}
              onValueChange={setSupportType}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={t("support.supportType")} />
              </SelectTrigger>
              <SelectContent dir={locale === "ar" ? "rtl" : "ltr"}>
                <SelectGroup>
                  {supportTypes.map((item) => (
                    <SelectItem key={item.value} value={item.value}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="subject">{t("support.subject")}</Label>
            <Input
              id="subject"
              placeholder={t("support.subject")}
              required
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">{t("support.description")}</Label>
            <Textarea
              id="description"
              placeholder={t("support.description")}
              className="h-60"
              required
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsSupportOpen(false)
                setSubject("")
                setDescription("")
              }}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="button"
              disabled={isSending || subject === "" || description === ""}
              onClick={onClickSend}
            >
              {t("common.send")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
